/**
 * Weather API Service for React
 * 
 * This service handles communication with the WordPress backend
 * which proxies requests to The Weather Company API.
 */

const API_ENDPOINT = '/wp-json/oht-weather/v1';

/**
 * Get current weather conditions for a location
 * @param {string} zipcode - ZIP code or location ID
 * @returns {Promise} - Promise with weather data
 */
export const getCurrentWeather = async (zipcode) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/current?zipcode=${zipcode}`,
      `/index.php?rest_route=/oht-weather/v1/current&zipcode=${zipcode}`,
      `/?rest_route=/oht-weather/v1/current&zipcode=${zipcode}`
    ];
    
    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (current):', url);
        response = await fetch(url);
        
        if (response.ok) {
          break;
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch current weather data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching current weather:', error);
    throw error;
  }
};

/**
 * Get forecast for a location
 * @param {string} zipcode - ZIP code or location ID
 * @param {number} days - Number of days to forecast (3, 5, 7, 10, 15)
 * @returns {Promise} - Promise with forecast data
 */
export const getForecast = async (zipcode, days = 5) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/forecast?zipcode=${zipcode}&days=${days}`,
      `/index.php?rest_route=/oht-weather/v1/forecast&zipcode=${zipcode}&days=${days}`,
      `/?rest_route=/oht-weather/v1/forecast&zipcode=${zipcode}&days=${days}`
    ];
    
    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (forecast):', url);
        response = await fetch(url);
        
        if (response.ok) {
          break;
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch forecast data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching forecast:', error);
    throw error;
  }
};

/**
 * Get radar data for a location
 * @param {string} zipcode - ZIP code or location ID
 * @returns {Promise} - Promise with radar data
 */
export const getRadar = async (zipcode) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/radar?zipcode=${zipcode}`,
      `/index.php?rest_route=/oht-weather/v1/radar&zipcode=${zipcode}`,
      `/?rest_route=/oht-weather/v1/radar&zipcode=${zipcode}`
    ];
    
    // Get WordPress nonce if available
    const wpNonce = window?.wpApiSettings?.nonce || '';
    
    // Prepare headers
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    
    // Add nonce header if available
    if (wpNonce) {
      headers['X-WP-Nonce'] = wpNonce;
    }
    
    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (radar):', url);
        response = await fetch(url, {
          method: 'GET',
          headers: headers,
          credentials: 'same-origin' // Send cookies for WordPress authentication
        });
        
        if (response.ok) {
          break;
        } else {
          console.warn(`API request failed with status: ${response.status} for URL: ${url}`);
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch radar data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching radar data:', error);
    throw error;
  }
};

/**
 * Get hourly forecast for a location
 * @param {string} zipcode - ZIP code or location ID
 * @returns {Promise} - Promise with hourly forecast data
 */
export const getHourlyForecast = async (zipcode) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/hourly?zipcode=${zipcode}`,
      `/index.php?rest_route=/oht-weather/v1/hourly&zipcode=${zipcode}`,
      `/?rest_route=/oht-weather/v1/hourly&zipcode=${zipcode}`
    ];

    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (hourly):', url);
        response = await fetch(url);  
        
        if (response.ok) {
          break;
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch hourly forecast data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching hourly forecast:', error);
    throw error;
  }
};

/**
 * Get weather alerts for a location
 * @param {string} zipcode - ZIP code or location ID
 * @returns {Promise} - Promise with alerts data
 */
export const getAlerts = async (zipcode) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/alerts?zipcode=${zipcode}`,
      `/index.php?rest_route=/oht-weather/v1/alerts&zipcode=${zipcode}`,
      `/?rest_route=/oht-weather/v1/alerts&zipcode=${zipcode}`
    ];
    
    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (alerts):', url);
        response = await fetch(url);
        
        // Check if the response status is OK (2xx)
        if (response.ok) {
          console.log('Successful response received for alerts');
          break; // Exit loop on success
        } else {
          // Log non-OK responses
          console.warn(`Received non-OK status (${response.status}) for URL: ${url}`);
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    // If no response was successful or the final response was not ok
    if (!response || !response.ok) {
      console.error('Failed to fetch alerts data after trying all URLs. Last status:', response?.status, 'Last error:', lastError);
      // Return an empty structure consistent with successful "no alerts" response
      return { alerts: [] }; 
    }
    
    // Now that we have an OK response, try to parse JSON
    try {
      const data = await response.json();
      console.log('Successfully parsed alerts JSON:', data);
      // Ensure the response has the expected structure, return empty if not
      if (data && data.alerts) {
          return data;
      } else {
          console.warn('Alerts response missing expected structure:', data);
          return { alerts: [] }; // Return consistent empty structure
      }
    } catch (jsonError) {
      console.error('Error parsing alerts JSON response:', jsonError);
      // Return empty data object if JSON parsing fails
      return { alerts: [] };
    }
  } catch (error) {
    console.error('General error fetching alerts:', error);
    // Return empty data object for any other errors
    return { alerts: [] };
  }
};

/**
 * Get a specific alert detail
 * @param {string} alertId - The detail key for the alert
 * @returns {Promise} - Promise with alert detail data
 */
export const getAlertDetail = async (alertId) => {
  try {
    // Try multiple possible URL patterns for WordPress REST API
    const urls = [
      `${API_ENDPOINT}/alert-detail?alertId=${encodeURIComponent(alertId)}`,
      `/index.php?rest_route=/oht-weather/v1/alert-detail&alertId=${encodeURIComponent(alertId)}`,
      `/?rest_route=/oht-weather/v1/alert-detail&alertId=${encodeURIComponent(alertId)}`
    ];
    
    let response = null;
    let lastError = null;
    
    // Try each URL pattern until one works
    for (const url of urls) {
      try {
        console.log('Trying WordPress API URL (alert detail):', url);
        response = await fetch(url);
        
        if (response.ok) {
          break;
        }
        
      } catch (error) {
        console.warn(`Fetch error on ${url}:`, error);
        lastError = error;
      }
    }
    
    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch alert detail data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching alert detail:', error);
    throw error;
  }
};

export default {
  getCurrentWeather,
  getForecast,
  getRadar,
  getHourlyForecast,
  getAlerts,
  getAlertDetail
}; 